import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useCycle } from '@/lib/cycle';
import { useAuth } from '@/lib/auth';
import { PageHeader, Card, Loading, Empty, SectionBadge } from '@/components/UI';
import { formatDate, formatDateTime } from '@/lib/format';
import { Plus, RefreshCw, X, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const DEPTS = ['quality', 'production', 'maintenance', 'supply_chain', 'hr_safety', 'environment', 'npd', 'business_sales'];

export default function ActionTracker() {
  const { currentCycle } = useCycle();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDept, setFilterDept] = useState('all');
  const [filterOwner, setFilterOwner] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [creating, setCreating] = useState(false);
  const [carrying, setCarrying] = useState(false);

  const refresh = async () => {
    if (!currentCycle) return;
    setLoading(true);
    try {
      const q = new URLSearchParams({ cycle_id: currentCycle.id });
      if (filterStatus !== 'all') q.append('status', filterStatus);
      if (filterDept !== 'all') q.append('department', filterDept);
      if (filterOwner) q.append('owner', filterOwner);
      const r = await api.get(`/actions?${q.toString()}`);
      setItems(r.data || []);
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line
  useEffect(() => { refresh(); }, [currentCycle?.id, filterStatus, filterDept]);

  const carryForward = async () => {
    if (!window.confirm('Carry forward all open actions from the previous cycle into this cycle?')) return;
    setCarrying(true);
    try {
      const r = await api.post(`/actions/carry-forward/${currentCycle.id}`);
      toast.success(`${r.data.carried} action(s) carried forward`);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setCarrying(false); }
  };

  if (!currentCycle) return <Empty title="No cycle selected" />;

  const counts = {
    total: items.length,
    open: items.filter((a) => a.status === 'open').length,
    in_progress: items.filter((a) => a.status === 'in_progress').length,
    closed: items.filter((a) => a.status === 'closed').length,
    overdue: items.filter((a) => a.status === 'overdue').length,
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Action Tracker" subtitle={`Cycle: ${currentCycle.label}`} actions={
        <div className="flex items-center gap-2">
          {user.role === 'admin' && <button className="btn-secondary text-sm" onClick={carryForward} disabled={carrying} data-testid="btn-carry-forward"><RefreshCw size={14} className="inline mr-1" />{carrying ? 'Working…' : 'Carry Forward'}</button>}
          <button className="btn-primary text-sm" onClick={() => setCreating(true)} data-testid="btn-new-action"><Plus size={14} className="inline mr-1" /> New Action</button>
        </div>
      } />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="p-3 rounded border border-[var(--aampl-border)] bg-white text-center"><div className="text-2xl font-bold aampl-navy-text">{counts.total}</div><div className="text-[11px] uppercase text-slate-500">Total</div></div>
        <div className="p-3 rounded border border-amber-200 bg-amber-50 text-center"><div className="text-2xl font-bold text-amber-700">{counts.open}</div><div className="text-[11px] uppercase text-amber-700">Open</div></div>
        <div className="p-3 rounded border border-blue-200 bg-blue-50 text-center"><div className="text-2xl font-bold text-blue-700">{counts.in_progress}</div><div className="text-[11px] uppercase text-blue-700">In Progress</div></div>
        <div className="p-3 rounded border border-emerald-200 bg-emerald-50 text-center"><div className="text-2xl font-bold text-emerald-700">{counts.closed}</div><div className="text-[11px] uppercase text-emerald-700">Closed</div></div>
        <div className="p-3 rounded border border-rose-200 bg-rose-50 text-center"><div className="text-2xl font-bold text-rose-700">{counts.overdue}</div><div className="text-[11px] uppercase text-rose-700">Overdue</div></div>
      </div>

      <Card>
        <div className="flex flex-wrap items-end gap-3 mb-3">
          <div><label className="aampl-label">Status</label>
            <select className="aampl-input" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="action-filter-status">
              <option value="all">All</option><option value="open">Open</option><option value="in_progress">In Progress</option><option value="closed">Closed</option><option value="overdue">Overdue</option>
            </select>
          </div>
          <div><label className="aampl-label">Department</label>
            <select className="aampl-input" value={filterDept} onChange={(e) => setFilterDept(e.target.value)}>
              <option value="all">All</option>{DEPTS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div><label className="aampl-label">Owner</label>
            <input className="aampl-input" value={filterOwner} onChange={(e) => setFilterOwner(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && refresh()} placeholder="Owner name" />
          </div>
          <button className="btn-ghost" onClick={refresh}>Apply</button>
        </div>
        {loading ? <Loading /> : items.length === 0 ? <div className="text-sm text-slate-500 text-center py-6">No actions match filters.</div> : (
          <div className="aampl-table-wrap">
            <table className="aampl-table">
              <thead><tr><th>Action #</th><th>Description</th><th>Owner</th><th>Dept</th><th>Target</th><th>Status</th><th>Priority</th><th>Origin</th><th></th></tr></thead>
              <tbody>
                {items.map((a) => (
                  <React.Fragment key={a.id}>
                    <tr className="cursor-pointer" onClick={() => setExpanded(expanded === a.id ? null : a.id)} data-testid={`action-row-${a.action_number}`}>
                      <td className="font-mono text-xs">{a.action_number}</td>
                      <td className="max-w-xs"><div className="line-clamp-2">{a.description}</div></td>
                      <td>{a.owner}</td>
                      <td className="uppercase text-xs">{a.department}</td>
                      <td>{formatDate(a.target_date)}</td>
                      <td><SectionBadge status={a.status} label={a.status} /></td>
                      <td className="uppercase text-xs">{a.priority}</td>
                      <td className="text-xs text-slate-500">{a.origin_cycle_id === currentCycle.id ? 'NEW' : 'Carried'}</td>
                      <td><ChevronRight size={14} className={`transition ${expanded === a.id ? 'rotate-90' : ''}`} /></td>
                    </tr>
                    {expanded === a.id && (
                      <tr><td colSpan={9} className="bg-slate-50"><ActionEditor action={a} onSaved={refresh} /></td></tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {creating && <CreateActionModal cycle={currentCycle} onClose={() => setCreating(false)} onDone={() => { setCreating(false); refresh(); }} />}
    </div>
  );
}

function ActionEditor({ action, onSaved }) {
  const [a, setA] = useState(action);
  const [closing, setClosing] = useState(false);
  const [eff, setEff] = useState(action.effectiveness_verification || '');
  const [evidence, setEvidence] = useState(action.evidence || '');
  const set = (k, v) => setA({ ...a, [k]: v });

  const save = async () => {
    try {
      await api.put(`/actions/${a.id}`, { description: a.description, owner: a.owner, department: a.department, target_date: a.target_date, status: a.status, priority: a.priority, iso_clause: a.iso_clause });
      toast.success('Saved');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  const close = async () => {
    if (!eff.trim()) { toast.error('Effectiveness verification required'); return; }
    setClosing(true);
    try {
      await api.post(`/actions/${a.id}/close`, { effectiveness_verification: eff, evidence });
      toast.success('Action closed');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Close failed'); } finally { setClosing(false); }
  };

  return (
    <div className="p-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="space-y-3 lg:col-span-2">
        <div><label className="aampl-label">Description</label><textarea className="aampl-input" rows={3} value={a.description} onChange={(e) => set('description', e.target.value)} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="aampl-label">Owner</label><input className="aampl-input" value={a.owner} onChange={(e) => set('owner', e.target.value)} /></div>
          <div><label className="aampl-label">Department</label>
            <select className="aampl-input" value={a.department} onChange={(e) => set('department', e.target.value)}>{DEPTS.map((d) => <option key={d}>{d}</option>)}</select>
          </div>
          <div><label className="aampl-label">Target Date</label><input className="aampl-input" type="date" value={a.target_date} onChange={(e) => set('target_date', e.target.value)} /></div>
          <div><label className="aampl-label">Status</label>
            <select className="aampl-input" value={a.status} onChange={(e) => set('status', e.target.value)} data-testid="action-edit-status">
              <option value="open">Open</option><option value="in_progress">In Progress</option><option value="closed">Closed</option><option value="overdue">Overdue</option>
            </select>
          </div>
          <div><label className="aampl-label">Priority</label>
            <select className="aampl-input" value={a.priority} onChange={(e) => set('priority', e.target.value)}>
              <option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
            </select>
          </div>
          <div><label className="aampl-label">ISO Clause</label><input className="aampl-input" value={a.iso_clause || ''} onChange={(e) => set('iso_clause', e.target.value)} /></div>
        </div>
        <div className="border-t border-[var(--aampl-border)] pt-3">
          <div className="font-semibold aampl-navy-text text-sm mb-2">Closure</div>
          <div className="space-y-2">
            <div><label className="aampl-label">Effectiveness Verification *</label><textarea className="aampl-input" rows={2} value={eff} onChange={(e) => setEff(e.target.value)} data-testid="action-effectiveness" /></div>
            <div><label className="aampl-label">Evidence</label><input className="aampl-input" value={evidence} onChange={(e) => setEvidence(e.target.value)} /></div>
          </div>
        </div>
        <div className="flex gap-2 pt-2">
          <button className="btn-primary" onClick={save}>Save Changes</button>
          {a.status !== 'closed' && <button className="btn-secondary" onClick={close} disabled={closing} data-testid="btn-close-action">{closing ? 'Closing…' : 'Close Action'}</button>}
        </div>
      </div>
      <div>
        <div className="text-sm font-semibold aampl-navy-text mb-2">History</div>
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {(a.history || []).slice().reverse().map((h, i) => (
            <div key={i} className="text-xs border-l-2 border-[var(--aampl-gold)] pl-2 py-1">
              <div className="font-semibold">{h.month_label || formatDateTime(h.updated_at)} — {h.status?.toUpperCase()}</div>
              <div className="text-slate-600">{h.remarks}</div>
              <div className="text-[10px] text-slate-400">{formatDateTime(h.updated_at)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CreateActionModal({ cycle, onClose, onDone }) {
  const [d, setD] = useState({ description: '', owner: '', department: 'production', target_date: '', priority: 'medium', iso_clause: '' });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!d.description.trim()) { toast.error('Description required'); return; }
    if (!d.owner.trim()) { toast.error('Owner required'); return; }
    if (!d.target_date) { toast.error('Target date required'); return; }
    setBusy(true);
    try {
      await api.post('/actions', { ...d, cycle_id: cycle.id });
      toast.success('Action created');
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 w-full max-w-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4"><h3 className="text-lg font-bold aampl-navy-text">New Action Item</h3><button className="btn-ghost" onClick={onClose}><X size={16} /></button></div>
        <div className="space-y-3">
          <div><label className="aampl-label">Description *</label><textarea className="aampl-input" rows={3} value={d.description} onChange={(e) => setD({...d, description: e.target.value})} data-testid="new-action-desc" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="aampl-label">Owner *</label><input className="aampl-input" value={d.owner} onChange={(e) => setD({...d, owner: e.target.value})} data-testid="new-action-owner" /></div>
            <div><label className="aampl-label">Department</label><select className="aampl-input" value={d.department} onChange={(e) => setD({...d, department: e.target.value})}>{DEPTS.map((dp) => <option key={dp}>{dp}</option>)}</select></div>
            <div><label className="aampl-label">Target Date *</label><input className="aampl-input" type="date" value={d.target_date} onChange={(e) => setD({...d, target_date: e.target.value})} data-testid="new-action-target" /></div>
            <div><label className="aampl-label">Priority</label><select className="aampl-input" value={d.priority} onChange={(e) => setD({...d, priority: e.target.value})}><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option></select></div>
          </div>
          <div><label className="aampl-label">ISO Clause</label><input className="aampl-input" value={d.iso_clause} onChange={(e) => setD({...d, iso_clause: e.target.value})} /></div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={submit} disabled={busy} data-testid="btn-create-action-submit">{busy ? 'Creating…' : 'Create'}</button>
        </div>
      </div>
    </div>
  );
}
